import React, { useEffect } from 'react';
import { enqueueSnackbar } from 'notistack';
import { ConfirmResponse, useConfirmDialog } from '_shared/_providers/dialogs/confirm/confirm-dialog';
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";

import Guid from '_shared/guids/guid';
import { styled, Theme } from '@mui/material/styles';
import SystemNotification from 'notifications/system-notifications/system-notification';
import { SystemMessageSeverity } from 'notifications/system-notifications/system-message-severity';
import Stack from '@mui/material/Stack';
import {
  useCreateSystemNotificationMutation,
  useDeleteSystemNotificationMutation,
  useGetSystemNotificationsQuery,
  useUpdateSystemNotificationMutation,
} from 'users/super-users/_api/super-user-api';

const NotificationsPaper = styled(Paper)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(1),
  position: 'relative'
}));
const NotificationsContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("xl")]: {
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto auto",
  },
  display: "grid",
  gridTemplateColumns: "1fr 2fr",
  gridTemplateRows: "1fr",
  width: "100%",
}));
const NotificationForm = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1),
}));
const NotificationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5)
}));
const NotificationFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  minWidth: '20rem'
}));
const NotificationInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  minWidth: '20rem'
}));
const NotificationSelect = styled(Select)(() => ({
  minWidth: '20rem'
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontSize: "1.3em",
}));
const getNotificationTypeMenuItemSx = (color: "info" | "warning" | "error") => (theme: Theme) => ({
  color: theme.palette[color].main,
});
const getNotificationTypeTextSx = (notificationType: SystemMessageSeverity | undefined) => {
  const color = notificationType ? getNotificationTypeColor(notificationType) : undefined;
  return color ? (theme: Theme) => ({ color: theme.palette[color].main }) : undefined;
};
function getNotificationTypeColor(notificationType: SystemMessageSeverity): "info" | "warning" | "error" | undefined {
  switch (notificationType) {
    case SystemMessageSeverity.Info:
      return "info";
    case SystemMessageSeverity.Warning:
      return "warning";
    case SystemMessageSeverity.Alert:
      return "error";
    default:
      return undefined;
  }
}
function getNotificationTypeLabel(notificationType: SystemMessageSeverity | undefined): string {
  if (!notificationType) return "";
  return notificationType.charAt(0).toUpperCase() + notificationType.slice(1);
}

export default function SystemNotifications() {
  const [notificationType, setNotificationType] = React.useState<SystemMessageSeverity | ''>('');
  const [notificationContent, setNotificationContent] = React.useState('');
  const [editingNotificationId, setEditingNotificationId] = React.useState<Guid | null>(null);
  const notificationsQuery = useGetSystemNotificationsQuery();
  const [createSystemNotification, { isLoading: isCreating }] = useCreateSystemNotificationMutation();
  const [updateSystemNotification, { isLoading: isUpdating }] = useUpdateSystemNotificationMutation();
  const [deleteSystemNotification, { isLoading: isDeleting }] = useDeleteSystemNotificationMutation();

  const confirm = useConfirmDialog();
  const messageIsValid = notificationContent.trim() !== '';
  const formValid = notificationType !== '' && messageIsValid;
  const selectedNotificationTypeColor = notificationType ? getNotificationTypeColor(notificationType) : undefined;
  const systemNotifications: SystemNotification[] = notificationsQuery.data ?? [];
  const saving = isCreating || isUpdating || isDeleting;

  useEffect(() => {
    if (!notificationsQuery.error) return;
    enqueueSnackbar("Unable to fetch system notifications...", { variant: 'error' });
  }, [notificationsQuery.error]);

  async function saveNotification() {
    if (!notificationType || !messageIsValid) return;

    try {
      if (editingNotificationId) {
        await updateSystemNotification({
          notificationId: editingNotificationId,
          notificationType,
          notificationContent,
        }).unwrap();
      } else {
        await createSystemNotification({ notificationType, notificationContent }).unwrap();
      }
      clearForm();
    }
    catch {
      enqueueSnackbar(`Failed to ${editingNotificationId ? "update" : "create"} system notification`, { variant: 'error' });
    }
  }

  async function deleteNotification(notificationId: Guid) {
    const response = await confirm({
      title: "Delete system notification?",
      message: "Are you sure you want to delete this system notification?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteSystemNotification({ notificationId }).unwrap();
    }
    catch {
      enqueueSnackbar("Failed to delete system notification", { variant: 'error' });
    }
  }

  function clearForm() {
    setNotificationType('');
    setNotificationContent('');
    setEditingNotificationId(null);
  }

  function editNotification(notification: SystemNotification) {
    setEditingNotificationId(notification.id);
    setNotificationType(notification.type ?? SystemMessageSeverity.Info);
    setNotificationContent(notification.message);
  }

  return (
    <NotificationsPaper>
      <NotificationsContainer>
        <NotificationForm>
          <NotificationFormTitle variant='h6'>
            {editingNotificationId ? 'Edit System Notification' : 'New System Notification'}
          </NotificationFormTitle>
          <NotificationFormControl required>
            <InputLabel id="notification-type-select-label">Notification Type</InputLabel>
            <NotificationSelect
              required
              labelId="notification-type-select-label"
              id="notification-type-select"
              value={notificationType}
              label="Notification Type"
              sx={
                selectedNotificationTypeColor
                  ? (theme) => ({ ".MuiSelect-select": { color: theme.palette[selectedNotificationTypeColor].main } })
                  : undefined
              }
              onChange={(event) => {
                setNotificationType(event.target.value as SystemMessageSeverity);
              }}>
              <MenuItem sx={getNotificationTypeMenuItemSx("info")} value={SystemMessageSeverity.Info}>Info</MenuItem>
              <MenuItem sx={getNotificationTypeMenuItemSx("warning")} value={SystemMessageSeverity.Warning}>Warning</MenuItem>
              <MenuItem sx={getNotificationTypeMenuItemSx("error")} value={SystemMessageSeverity.Alert}>Alert</MenuItem>
            </NotificationSelect>
          </NotificationFormControl>
          <NotificationInput
            required
            label="Message"
            minRows={5}
            multiline
            value={notificationContent}
            variant="outlined"
            error={notificationContent.length > 0 && !messageIsValid}
            helperText={notificationContent.length > 0 && !messageIsValid ? "Message cannot be empty." : undefined}
            onChange={(event) => {
              setNotificationContent(event.target.value);
            }}
          />
          <Button
            color="primary"
            variant="contained"
            disabled={saving || !formValid}
            onClick={saveNotification}>
            {editingNotificationId ? 'Save Changes' : 'Send Notification'}
          </Button>
          {editingNotificationId && (
            <Button color="inherit" onClick={clearForm} disabled={saving}>Cancel</Button>
          )}
        </NotificationForm>

        <NotificationForm>
          <NotificationFormTitle variant='h6'>
            Current System Notifications
          </NotificationFormTitle>
          {systemNotifications &&
            <Table aria-label="notification table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">Type</TableCell>
                  <TableCell align="left">Message</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {systemNotifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <NoRowsPlaceholder>
                        No current system notifications
                      </NoRowsPlaceholder>
                    </TableCell>
                  </TableRow>
                ) : (
                  systemNotifications.map((row) => (
                    <TableRow key={row.id.value}>
                      <TableCell
                        align="left"
                        sx={getNotificationTypeTextSx(row.type)}
                      >
                        {getNotificationTypeLabel(row.type)}
                      </TableCell>
                      <TableCell align="left">{row.message}</TableCell>
                      <TableCell align='right'>
                        <Button
                          onClick={() => editNotification(row)}
                          color="primary">
                          Edit
                        </Button>
                        <Button
                          onClick={() => deleteNotification(row.id)}
                          color="primary">
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          }
        </NotificationForm>
      </NotificationsContainer>
    </NotificationsPaper>
  )
}
